import React, { useEffect, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { useAuthContext, useRefresh } from '../../../../context/auth.context'
import { useApiFormAuthInvite, useApiListAuthOrganization } from '../../../../api-new/state/auth'
import { AuthHead, AuthLoading, AuthPage } from '../../../../shared/sso-workspace/auth-card'
import { InviteConfirmation } from '../../../../shared/sso-workspace/invite-confirmation'
import { usePendingInvites } from '../../../../shared/sso-workspace/pending-invites'
import { useLoginRedirect } from '../hooks/redirect'
import { goBackOr } from '../../../../shared/hooks/go-back'

// /auth/invitations: pending invitations while signed in (owner feedback
// 2026-09-27) -- no sign-out needed. From the account menu ("Invitations",
// with a count) and the Switch organization page's notice. The same cards as
// the invitations step after a single sign-on; Accept / Decline go to the
// AUTHENTICATED endpoints (POST /auth-invite/:userID/accept|decline), bound to
// the signed-in login, never an invite key. An impersonating administrator
// is shown none and can't act (the server refuses too).
//
// "Open <org>" on an accepted card switches into that organization through
// the normal organization switch (PUT /auth-organization/:refreshToken with
// the invite's user row), recorded as the person's choice.

export function Invitations() {
  const authContext = useAuthContext()
  const history = useHistory()
  const apiFormAuthInvite = useApiFormAuthInvite()
  const apiListAuthOrganization = useApiListAuthOrganization()
  const loginRedirect = useLoginRedirect()
  const pending = usePendingInvites()
  const { refresh } = useRefresh()

  // The page reads fresh on open (the shared count may be up to a minute
  // old) and keeps the list it opened with, so a decided card stays on
  // screen with its outcome (and "Open <org>") instead of vanishing.
  const [shown, setShown] = useState(null)
  const signedIn = !!authContext.state.accessToken
  useEffect(
    () => {
      let live = true
      if (signedIn) pending.reload().then(list => live && setShown(list))
      return () => {
        live = false
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [signedIn]
  )
  const invites = shown

  const handleDecide = (invite, accept) => apiFormAuthInvite.postArg(`/${invite.userID}/${accept ? 'accept' : 'decline'}`, {})

  const handleOpen = async invite => {
    const authToken = await apiListAuthOrganization.put(authContext.state.refreshToken, {
      organizationID: invite.organizationID,
      userID: invite.userID,
      name: invite.organizationName,
      chosen: true
    })
    authContext.setAuthToken(authToken)
    loginRedirect({ skipChoice: true })
  }

  // An accepted invitation is a new organization to switch to: refresh the
  // token now, so its multipleAccounts (the sidebar card's and the account
  // menu's switch, shared/organization-switch) counts it. Best effort: the
  // next refresh catches up anyway.
  const handleDecided = (invite, accept) => {
    pending.reload()
    if (accept) Promise.resolve().then(refresh).catch(() => {})
  }

  const handleDone = () => goBackOr(history)

  if (!authContext.state.accessToken) {
    return (
      <AuthPage labelledBy="invites-title">
        <AuthHead id="invites-title" title="Your invitations" lede="Sign in to see the invitations waiting for you." />
        <Link replace className="btn sw-btn-primary sa-block" to="/auth/email">Sign in</Link>
      </AuthPage>
    )
  }

  if (!invites) {
    return (
      <AuthPage labelledBy="invites-title">
        <AuthHead id="invites-title" title="Your invitations" />
        <AuthLoading>Finding your invitations…</AuthLoading>
      </AuthPage>
    )
  }

  if (!invites.length) {
    return (
      <AuthPage labelledBy="invites-title">
        <AuthHead id="invites-title" title="Your invitations" lede="You have no pending invitations. When an organization invites you, it shows up here." />
        <button type="button" className="btn sw-btn sa-block" onClick={handleDone}>
          Back
        </button>
      </AuthPage>
    )
  }

  return (
    <InviteConfirmation
      invites={invites}
      title="Your invitations"
      lede="These organizations invited you to EducationXR. Nothing changes until you accept."
      continueLabel="Done"
      onDecide={handleDecide}
      onDecided={handleDecided}
      onOpen={handleOpen}
      onContinue={handleDone}
    />
  )
}
